import { useEffect, useState, type ReactNode } from 'react';
import { MonitorX, RefreshCw, WifiOff } from 'lucide-react';
import { LoginShell } from '@/auth/LoginShell';
import { Button } from '@/components/Button';
import { APP_NAME } from '@/components/PageHeader';
import { ApiError, setAccessBlockedHandler } from '@/lib/api';
import './security.css';

/**
 * IP アドレス制限・端末証明書で利用できない場合（API が 403 IP_NOT_ALLOWED / DEVICE_CERT_* を返した場合）に、
 * 画面全体を案内に切り替える。App の最上位（PcOnlyGuard の内側）に配置する。
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const [blocked, setBlocked] = useState<ApiError | null>(null);
  useEffect(() => {
    setAccessBlockedHandler((e) => setBlocked(e));
    return () => setAccessBlockedHandler(null);
  }, []);
  if (blocked) return <AccessBlockedPage error={blocked} />;
  return <>{children}</>;
}

export function AccessBlockedPage({ error }: { error: ApiError }) {
  const isIp = error.code === 'IP_NOT_ALLOWED';
  const title = isIp ? 'このネットワークからは利用できません' : 'この端末は登録されていません';
  useEffect(() => {
    document.title = `${title}｜${APP_NAME}`;
  }, [title]);

  return (
    <LoginShell>
      <div className="bs-blocked" role="alert" data-code={error.code}>
        <span className="bs-blocked__icon" aria-hidden>
          {isIp ? <WifiOff size={26} /> : <MonitorX size={26} />}
        </span>
        <h1 className="bs-login__title bs-login__title--sm">{title}</h1>
        <p className="bs-blocked__message">{error.message}</p>
        {isIp ? (
          <ul className="bs-login__notes bs-blocked__notes">
            <li>本システムは、所属組織ごとに登録されたネットワーク（事務所の回線など）からのみ利用できます。</li>
            <li>普段と異なる場所・回線（自宅、モバイル回線、公衆 Wi-Fi 等）から接続していないか確認してください。</li>
            <li>解決しない場合は、表示されている接続元 IP アドレスを添えて、振興基金の担当者へお問い合わせください。</li>
          </ul>
        ) : (
          <ul className="bs-login__notes bs-blocked__notes">
            <li>本システムは、振興基金が発行・登録した端末証明書を取り込んだパソコンからのみ利用できます。</li>
            <li>ブラウザを起動し直し、証明書の選択画面が表示されたら「blue-shield」の証明書を選択してください。</li>
            <li>新しいパソコン・証明書の期限切れ・紛失の場合は、振興基金の担当者に証明書の発行（再発行）を依頼してください。</li>
          </ul>
        )}
        <div className="bs-mfa-actions bs-mfa-actions--start">
          <Button variant="primary" icon={<RefreshCw size={16} />} onClick={() => window.location.assign('/login')}>
            再読み込み
          </Button>
        </div>
      </div>
    </LoginShell>
  );
}
